import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ProfesionalProduction } from '@/application/metrics';
import { computeClinicBenchmark } from '@/application/metrics/professionalClinicBenchmark';
import { professionalDetailCrossLinks } from '@/application/metrics/professionalDetailCrossLinks';
import { persistReportScope } from '@/application/metrics/metricsScope';
import { breadcrumbHref } from '@/application/navigation/breadcrumbScope';
import {
  getProfesionalPvpProfile,
  getProfesionalesMetrics,
  type ProfesionalPvpProfile,
} from '@/infrastructure/http/metricsApi';
import { AccessDenied } from '@/presentation/components/common/AccessDenied';
import { Breadcrumbs } from '@/presentation/components/common/Breadcrumbs';
import { canAccessClinicHub, firstVisibleHousePath } from '@/application/navigation/menuHouses';
import { DomainPageHeader } from '@/presentation/components/domain/DomainPageHeader';
import { ProfessionalClinicBenchmarkPanel } from '@/presentation/components/domain/ProfessionalClinicBenchmarkPanel';
import { ProfessionalDetailCrossLinks } from '@/presentation/components/domain/ProfessionalDetailCrossLinks';
import { LoadingBlock } from '@/presentation/components/common/LoadingBlock';
import { MetricKpiCard } from '@/presentation/components/metrics/MetricKpiCard';
import { OwnerStatusBadge } from '@/presentation/components/metrics/OwnerStatusBadge';
import { presentRetentionStatus } from '@/application/metrics/pvpRetentionPresentation';
import { EmailReportButton } from '@/presentation/components/reports/EmailReportButton';
import { formatCurrency, formatInteger } from '@/presentation/components/metrics/metricFormatters';
import { useGlobalPeriod } from '@/presentation/hooks/useGlobalPeriod';
import { formatSigned } from '@/presentation/utils/metricDeltas';
import { useAuth } from '@/presentation/contexts/useAuth';
import { usePermissions } from '@/presentation/contexts/permissionsContextCore';
import { useClinicNames } from '@/presentation/hooks/useClinicNames';

function buildSparklinePath(values: Array<number | null>, width: number, height: number): string {
  const points = values
    .map((value, index) => ({ value, index }))
    .filter((point): point is { value: number; index: number } => point.value !== null);
  if (points.length === 0) return '';
  const min = Math.min(...points.map((p) => p.value));
  const max = Math.max(...points.map((p) => p.value));
  const span = Math.max(0.0001, max - min);
  const stepX = points.length <= 1 ? 0 : width / (points.length - 1);
  return points
    .map((point, visibleIndex) => {
      const x = visibleIndex * stepX;
      const y = height - ((point.value - min) / span) * height;
      return `${visibleIndex === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ');
}

/** Detalle de un profesional: actual → mes anterior → histórico + comparación clínica/red. */
export function ProfessionalDetailPage() {
  const { profesionalId: profesionalIdParam = '' } = useParams<{ profesionalId: string }>();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { hasPermission } = usePermissions();
  const clinicNames = useClinicNames();
  const { period, year, month, label: periodLabel } = useGlobalPeriod();
  const locale = i18n.language;

  const profesionalId = Number(profesionalIdParam);
  const tenantFromQuery = searchParams.get('tenant') ?? '';
  const tenantIds = useMemo(
    () => user?.scope.allowedTenantIds ?? [],
    [user?.scope.allowedTenantIds]
  );
  const [tenantId, setTenantId] = useState(tenantFromQuery || tenantIds[0] || '');

  const [profile, setProfile] = useState<ProfesionalPvpProfile | null>(null);
  const [production, setProduction] = useState<ProfesionalProduction | null>(null);
  const [clinicRows, setClinicRows] = useState<ProfesionalProduction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canView = hasPermission('metrics.profesionales');
  const inScope = Boolean(tenantId && tenantIds.includes(tenantId));

  useEffect(() => {
    if (tenantFromQuery && tenantIds.includes(tenantFromQuery)) {
      setTenantId(tenantFromQuery);
    }
  }, [tenantFromQuery, tenantIds]);

  useEffect(() => {
    let cancelled = false;
    if (!token || !canView || !inScope || !Number.isFinite(profesionalId) || profesionalId <= 0) {
      setProfile(null);
      setProduction(null);
      setClinicRows([]);
      return;
    }
    persistReportScope(period, tenantId);

    void (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [pvpProfile, prodMetrics] = await Promise.all([
          getProfesionalPvpProfile(token, {
            tenantId,
            profesionalId,
            year,
            month,
            months: 6,
          }),
          getProfesionalesMetrics(token, { tenantId, year, month }).catch(() => null),
        ]);
        if (cancelled) return;
        setProfile(pvpProfile);
        const rows = prodMetrics?.profesionales ?? [];
        setClinicRows(rows);
        const match = rows.find((row) => row.profesionalId === profesionalId) ?? null;
        setProduction(match);
      } catch (err) {
        if (!cancelled) {
          setProfile(null);
          setProduction(null);
          setClinicRows([]);
          setError(err instanceof Error ? err.message : t('metrics.states.error.message'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, canView, inScope, profesionalId, tenantId, year, month, period, t]);

  const sparkPath = useMemo(
    () => (profile ? buildSparklinePath(profile.pvp.trendValues, 280, 64) : ''),
    [profile]
  );

  const clinicBenchmark = useMemo(
    () =>
      computeClinicBenchmark({
        profesionalId,
        rows: clinicRows,
      }),
    [profesionalId, clinicRows]
  );

  const crossLinks = useMemo(
    () =>
      professionalDetailCrossLinks({
        tenantId,
        period,
        hasPermission,
      }).filter((link) => link.id !== 'clinic'),
    [tenantId, period, hasPermission]
  );

  const canOpenClinic = canAccessClinicHub(hasPermission);

  const clinicDeltaPct =
    profile && profile.clinic.pvp > 0
      ? ((profile.pvp.actual - profile.clinic.pvp) / profile.clinic.pvp) * 100
      : null;
  const networkDeltaPct =
    profile && profile.network.pvpAverage && profile.network.pvpAverage > 0
      ? ((profile.pvp.actual - profile.network.pvpAverage) / profile.network.pvpAverage) * 100
      : null;

  const emailScope = useMemo(() => {
    if (!profile) return null;
    return {
      kind: 'generic' as const,
      year,
      month,
      tenantId,
      title: `${profile.nombre} · ${period}`,
      description: clinicNames[tenantId] ?? tenantId,
      kpis: [
        { label: 'PVP', value: profile.pvp.actual },
        {
          label: t('domain.professionals.detail.pvpPrevious'),
          value: profile.pvp.mesAnterior ?? '—',
        },
        {
          label: t('domain.professionals.detail.pvpHist'),
          value: profile.pvp.promedioHistorico ?? '—',
        },
        {
          label: t('domain.professionals.detail.appointments'),
          value: production?.turnosAtendidos ?? '—',
        },
        {
          label: t('domain.professionals.detail.revenue'),
          value: production ? Math.round(production.ingresos) : '—',
        },
      ],
      tables: [
        {
          title: t('domain.professionals.detail.trendTitle', { months: profile.monthsAnalyzed }),
          columns: [
            { key: 'period', header: t('period.label') },
            { key: 'pvp', header: 'PVP' },
          ],
          rows: profile.pvp.trendPeriods.map((p, index) => ({
            period: p,
            pvp: profile.pvp.trendValues[index] ?? '—',
          })),
        },
      ],
    };
  }, [profile, production, year, month, tenantId, period, clinicNames, t]);

  if (!canView) {
    return <AccessDenied homeTo={firstVisibleHousePath(hasPermission)} />;
  }

  if (!inScope) {
    return (
      <div className="panel-page space-y-3">
        <p className="text-sm text-gray-600 dark:text-gray-400">{t('clinicHub.outOfScope.message')}</p>
        <Link to="/professionals" className="text-sm font-semibold text-teal-700 hover:underline dark:text-teal-300">
          {t('domain.professionals.backList')}
        </Link>
      </div>
    );
  }

  const clinicName = clinicNames[tenantId] ?? tenantId;
  const title = profile?.nombre ?? `#${profesionalId}`;
  const statusPresentation = profile ? presentRetentionStatus(profile.pvp.status) : null;
  const attention =
    statusPresentation?.tone === 'critical' || statusPresentation?.tone === 'watch';

  return (
    <div className="panel-page panel-safe-bottom space-y-5">
      <Breadcrumbs
        label={t('breadcrumbs.nav')}
        items={[
          {
            label: t('breadcrumbs.professionals'),
            to: breadcrumbHref('/professionals', { period, tenantId }),
          },
          { label: title },
        ]}
      />
      <DomainPageHeader
        question={t('domain.professionals.detail.question')}
        title={title}
        subtitle={t('domain.professionals.detail.subtitle', {
          clinic: clinicName,
          period: periodLabel,
        })}
      >
        <div className="flex flex-wrap items-center gap-2">
          {emailScope && <EmailReportButton scope={emailScope} />}
          <button
            type="button"
            onClick={() => navigate(`/professionals?period=${period}`)}
            className="min-h-11 rounded-md border border-[var(--bi-line)] px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-white dark:text-gray-200 sm:min-h-0 sm:py-1.5"
          >
            {t('domain.professionals.backList')}
          </button>
          {canOpenClinic ? (
            <Link
              to={`/clinics/${tenantId}?period=${period}&tab=professionals`}
              className="inline-flex min-h-11 items-center rounded-md border border-[var(--bi-line)] px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-white dark:text-gray-200 sm:min-h-0 sm:py-1.5"
            >
              {t('domain.professionals.openClinic')}
            </Link>
          ) : null}
        </div>
      </DomainPageHeader>

      {error && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-300">
          {error}
        </p>
      )}

      {isLoading ? (
        <div className="rounded-xl border border-[var(--bi-line)] bg-white/80 dark:bg-white/5">
          <LoadingBlock minHeightClassName="min-h-[10rem]" />
        </div>
      ) : profile ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            {statusPresentation ? (
              <OwnerStatusBadge
                label={t(statusPresentation.labelKey)}
                tip={t(statusPresentation.tipKey)}
                tone={statusPresentation.tone}
                testId="professional-retention-status"
              />
            ) : null}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <MetricKpiCard
              label={t('domain.professionals.detail.pvpCurrent')}
              value={profile.pvp.actual.toLocaleString(locale, { maximumFractionDigits: 1 })}
              detail={
                profile.pvp.variacionPct === null
                  ? t('dashboard.executive.noBaseline')
                  : `${formatSigned(locale, profile.pvp.variacionPct)}% ${t('dashboard.executive.vsPreviousMonth')}`
              }
              tone={attention ? 'amber' : 'green'}
            />
            <MetricKpiCard
              label={t('domain.professionals.detail.pvpPrevious')}
              value={
                profile.pvp.mesAnterior === null
                  ? '—'
                  : profile.pvp.mesAnterior.toLocaleString(locale, { maximumFractionDigits: 1 })
              }
              tone="slate"
            />
            <MetricKpiCard
              label={t('domain.professionals.detail.pvpHist')}
              value={
                profile.pvp.promedioHistorico === null
                  ? '—'
                  : profile.pvp.promedioHistorico.toLocaleString(locale, { maximumFractionDigits: 1 })
              }
              detail={
                profile.pvp.promedioHistorico === null
                  ? undefined
                  : `${formatSigned(
                      locale,
                      ((profile.pvp.actual - profile.pvp.promedioHistorico) / profile.pvp.promedioHistorico) * 100
                    )}% vs hist.`
              }
              tone="indigo"
            />
            <MetricKpiCard
              label={t('domain.professionals.detail.appointments')}
              value={production ? formatInteger(production.turnosAtendidos, locale) : '—'}
              detail={
                production?.deltaTurnosPct == null
                  ? undefined
                  : `${formatSigned(locale, production.deltaTurnosPct)}% turnos`
              }
              tone="blue"
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <MetricKpiCard
              label={t('domain.professionals.detail.revenue')}
              value={production ? formatCurrency(production.ingresos, locale) : '—'}
              detail={
                production?.deltaIngresosPct == null
                  ? undefined
                  : `${formatSigned(locale, production.deltaIngresosPct)}% ${t('dashboard.executive.vsPreviousMonth')}`
              }
              tone="green"
            />
            <MetricKpiCard
              label={t('domain.professionals.detail.vsClinic')}
              value={
                clinicDeltaPct === null
                  ? `${profile.clinic.pvp.toLocaleString(locale, { maximumFractionDigits: 1 })} PVP clín.`
                  : `${formatSigned(locale, clinicDeltaPct)}%`
              }
              detail={t('domain.professionals.detail.clinicPvp', {
                value: profile.clinic.pvp.toLocaleString(locale, { maximumFractionDigits: 1 }),
              })}
              tone="slate"
            />
            <MetricKpiCard
              label={t('domain.professionals.detail.vsNetwork')}
              value={
                networkDeltaPct === null
                  ? profile.network.pvpAverage === null
                    ? '—'
                    : profile.network.pvpAverage.toLocaleString(locale, { maximumFractionDigits: 1 })
                  : `${formatSigned(locale, networkDeltaPct)}%`
              }
              detail={
                profile.network.pvpAverage === null
                  ? undefined
                  : t('domain.professionals.detail.networkPvp', {
                      value: profile.network.pvpAverage.toLocaleString(locale, {
                        maximumFractionDigits: 1,
                      }),
                    })
              }
              tone="indigo"
            />
          </div>

          <ProfessionalClinicBenchmarkPanel
            benchmark={clinicBenchmark}
            clinicName={clinicName}
            periodLabel={periodLabel}
            locale={locale}
          />

          <ProfessionalDetailCrossLinks links={crossLinks} />

          <section className="rounded-xl border border-[var(--bi-line)] bg-white/80 p-4 dark:bg-white/5">
            <h2 className="text-sm font-semibold text-gray-950 dark:text-white">
              {t('domain.professionals.detail.trendTitle', { months: profile.monthsAnalyzed })}
            </h2>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t('domain.professionals.detail.trendRange', {
                best:
                  profile.pvp.mejorReciente === null
                    ? '—'
                    : profile.pvp.mejorReciente.toLocaleString(locale, { maximumFractionDigits: 1 }),
                worst:
                  profile.pvp.peorReciente === null
                    ? '—'
                    : profile.pvp.peorReciente.toLocaleString(locale, { maximumFractionDigits: 1 }),
                declines: profile.pvp.mesesConsecutivosDescenso,
              })}
            </p>
            <div className="mt-3 overflow-x-auto">
              <svg
                width={280}
                height={64}
                viewBox="0 0 280 64"
                className="text-teal-600 dark:text-teal-300"
                role="img"
                aria-label={t('domain.professionals.detail.trendAria')}
              >
                <path
                  d="M 0 63 L 280 63"
                  stroke="currentColor"
                  className="text-gray-200 dark:text-gray-700"
                  strokeWidth="1"
                  fill="none"
                />
                {sparkPath ? (
                  <path d={sparkPath} stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                ) : null}
              </svg>
            </div>
            <div className="mt-2 flex flex-wrap gap-2 text-[11px] tabular-nums text-gray-500 dark:text-gray-400">
              {profile.pvp.trendPeriods.map((periodo, index) => (
                <span key={periodo} className="rounded bg-slate-100 px-1.5 py-0.5 dark:bg-gray-800">
                  {periodo.slice(5)}:{' '}
                  {profile.pvp.trendValues[index] === null
                    ? '—'
                    : Number(profile.pvp.trendValues[index]).toLocaleString(locale, {
                        maximumFractionDigits: 1,
                      })}
                </span>
              ))}
            </div>
          </section>

          {profile.pvp.reasons.length > 1 && (
            <ul className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
              {profile.pvp.reasons.map((reason) => (
                <li key={reason}>• {reason}</li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <p className="text-sm text-gray-500">{t('domain.professionals.detail.empty')}</p>
      )}
    </div>
  );
}
